import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { css } from '@emotion/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addDays } from 'date-fns';
import { z } from 'zod';

import { createCouponQR, updateCouponQR } from '~/api/microsites/coupon';
import { qrcodeAPIService } from '~/api/qrcode';
import BuilderSecondStepLayout from '~/components/builder/BuilderSecondStepLayout';
import Loader from '~/components/ui/Loader';
import { FONTS, PREVIEW_BAR_VIEW_TYPE } from '~/constants/common';
import { COUPON_TEMPLATE_COLORS } from '~/constants/coupon';
import {
  COUPON_QR_ID_PARAM_NAME,
  QR_CODE_BUILDER_EDIT_MODE_PARAM_NAME,
  QR_CODE_ID_PARAM_NAME,
  QRCODE_DYNAMIC_MICROSITE_TYPE,
} from '~/constants/qr-code';
import { qrCodeKeys } from '~/constants/queryKeys';
import { LOCATION_TYPE } from '~/constants/vCard';
import useCouponQrData from '~/hooks/useCouponQrData';
import usePrefillQrCodeData from '~/hooks/usePrefillQrCodeData';
import useQrCodeData from '~/hooks/useQrCodeData';
import CreateQrDesignQRStepV2 from '~/pages/CreateQR/components/CreateQrDesignQRStepV2';
import CouponTemplateAppearance from '~/pages/CreateQR/sub-components/couponTemplate/CouponTemplateAppearance';
import CouponTemplateBasicInformation from '~/pages/CreateQR/sub-components/couponTemplate/CouponTemplateBasicInformation';
import useBuilderStore from '~/store/useBuilderStore';
import useCouponTemplateStore from '~/store/useCouponTemplateStore';
import { CreateCouponQrPayload, UpdateCouponQrPayload } from '~/types/coupon';
import { DynamicQrCodeData, UpdateQrCodeDataPayload } from '~/types/qrCode';
import { getDefaultAddress, getUpdateDynamicQrCodePayload } from '~/utils/builder';
import getReactHookFormErrorsRefNames from '~/utils/getReactHookFormErrorsRefNames';
import {
  getBuilderLocationSchema,
  getColorValidation,
  getGeneralBuilderSchema,
} from '~/utils/validation/qrCodeBuilder';
import { getUrlZodValidation } from '~/utils/validation/url';

export default function CouponTemplate() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const couponId = searchParams.get(COUPON_QR_ID_PARAM_NAME);
  const qrCodeId = searchParams.get(QR_CODE_ID_PARAM_NAME);
  const isEditMode = searchParams.get(QR_CODE_BUILDER_EDIT_MODE_PARAM_NAME);

  const { data: qrCodeData, isLoading: isLoadingQrCodeData } = useQrCodeData<DynamicQrCodeData>({ qrCodeId });

  const couponQRSchema = z
    .object({
      appearance: z.object({
        design: z.object({
          primary: getColorValidation({ t }),
          secondary: getColorValidation({ t }),
        }),
        fonts: z.object({
          title: z.string(),
          text: z.string(),
          titleColor: getColorValidation({ t }),
          textsColor: getColorValidation({ t }),
        }),
      }),
      basicInfo: z.object({
        offer: z.object({
          company: z.string(),
          title: z.string().min(1, { message: t('inputs:required') }),
          description: z.string(),
          salesBadge: z.string(),
          codeButton: z.string().min(1, { message: t('inputs:required') }),
        }),
        coupon: z
          .object({
            useBarcode: z.boolean(),
            code: z.string(),
            validUntil: z.date(),
            terms: z.string(),
            buttonText: z.string().min(1, { message: t('inputs:required') }),
            url: getUrlZodValidation({ t }),
            location: getBuilderLocationSchema({ t }),
          })
          .superRefine((data, ctx) => {
            if (!data.useBarcode && !data.code?.length) {
              ctx.addIssue({
                path: ['code'],
                code: z.ZodIssueCode.custom,
                message: t('inputs:required'),
              });
            }
          }),
      }),
    })
    .merge(getGeneralBuilderSchema({ t, prevQRCodeURLFromResponse: qrCodeData?.uri }));

  type CouponQRSchema = z.infer<typeof couponQRSchema>;

  const styles = useStyles();
  const { setActiveStep, activeStep, setPreviewViewType } = useBuilderStore();
  const {
    locationType,
    banner,
    barcode,
    showCompleteUrl,
    setCreateOrUpdateQrError,
    createOrUpdateQRError,
    setLocationType,
    setIsManuallyLocationCompleteAddress,
    setBanner,
    setBarcode,
  } = useCouponTemplateStore();

  const { mutateAsync: createCouponQRMutate, isPending: isPendingCreateCouponQR } = useMutation({
    mutationFn: createCouponQR,
    onSuccess: (response) => {
      searchParams.set(COUPON_QR_ID_PARAM_NAME, response.data.id);
      setSearchParams(searchParams);

      if (banner.imageId && !banner.isImageIdSentToDB) {
        setBanner({ imageId: null, isImageIdSentToDB: true });
      }

      if (barcode.imageId && !barcode.isImageIdSentToDB) {
        setBarcode({ imageId: null, isImageIdSentToDB: true });
      }
    },
    onError: (error: any) => {
      setCreateOrUpdateQrError(error);
    },
  });
  const { mutateAsync: updateCouponQRMutate, isPending: isPendingUpdateCouponQR } = useMutation({
    mutationFn: updateCouponQR,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qrCodeKeys.couponQrData });

      if (banner.imageId && !banner.isImageIdSentToDB) {
        setBanner({ imageId: null, isImageIdSentToDB: true });
      }

      if (barcode.imageId && !barcode.isImageIdSentToDB) {
        setBarcode({ imageId: null, isImageIdSentToDB: true });
      }
    },
    onError: (error: any) => {
      setCreateOrUpdateQrError(error);
    },
  });
  const { mutateAsync: updateQrCodeMutateAsync, isPending: isPendingUpdateQrCode } = useMutation({
    mutationFn: (body: UpdateQrCodeDataPayload) => qrcodeAPIService.updateQrCode(body, qrCodeId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qrCodeKeys.qrCodeData });
    },
  });

  const { data: couponQrData, isLoading: isLoadingCouponQrData } = useCouponQrData({ couponId });

  const isCouponData = !!couponQrData;
  const isQRCodeData = !!qrCodeData;

  const form = useForm<CouponQRSchema>({
    defaultValues: {
      qrCodeName: '',
      appearance: {
        design: {
          primary: COUPON_TEMPLATE_COLORS[0][0],
          secondary: COUPON_TEMPLATE_COLORS[0][1],
        },
        fonts: {
          title: FONTS[3].value,
          text: FONTS[3].value,
          titleColor: '#F18430',
          textsColor: '#FFFFFF',
        },
      },
      basicInfo: {
        offer: {
          company: 'Eshop',
          title: '25% OFF',
          description: 'Use this coupon in all categories',
          salesBadge: '25% OFF',
          codeButton: 'Get Coupon',
        },
        coupon: {
          useBarcode: false,
          code: 'SALE25OFF',
          validUntil: addDays(new Date(), 3),
          terms:
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nunc vulputate libero et velit interdum, ac aliquet odio mattis. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Curabitur tempus urna at turpis condimentum lobortis.',
          buttonText: 'Redeem Now',
          url: 'https://www.example.com/',
          location: {
            complete: {
              searchAddress: '',
              city: '',
              country: '',
              postalCode: '',
              state: '',
              street: '',
              number: '',
            },
            coordinates: {
              longitude: '',
              latitude: '',
            },
          },
        },
      },
      settings: {
        isAutoGeneratedUrl: true,
        isScanLimitsEnabled: false,
      },
    },
    resolver: zodResolver(couponQRSchema),
  });

  useEffect(() => {
    if (isCouponData && isEditMode) {
      // Appearance - Design
      form.setValue('appearance.design.primary', couponQrData.design.primary);
      form.setValue('appearance.design.secondary', couponQrData.design.secondary);
      // Appearance - Fonts
      form.setValue('appearance.fonts.title', couponQrData.fonts.title);
      form.setValue('appearance.fonts.text', couponQrData.fonts.text);
      form.setValue('appearance.fonts.titleColor', couponQrData.fontsColors.title);
      form.setValue('appearance.fonts.textsColor', couponQrData.fontsColors.text);
      // Basic info - Offer
      form.setValue('basicInfo.offer.company', couponQrData.companyName);
      form.setValue('basicInfo.offer.title', couponQrData.title);
      form.setValue('basicInfo.offer.description', couponQrData.description);
      form.setValue('basicInfo.offer.salesBadge', couponQrData.badgeText);
      form.setValue('basicInfo.offer.codeButton', couponQrData.showCodeButtonText);
      // Basic info - Coupon
      form.setValue('basicInfo.coupon.useBarcode', couponQrData.useBarcode);
      form.setValue('basicInfo.coupon.code', couponQrData.couponCode);
      form.setValue('basicInfo.coupon.validUntil', new Date(couponQrData.validUntilRaw));
      form.setValue('basicInfo.coupon.terms', couponQrData.termsAndConditions);
      form.setValue('basicInfo.coupon.buttonText', couponQrData.buttonText);
      form.setValue('basicInfo.coupon.url', couponQrData.buttonUrl);

      if (couponQrData.location) {
        setLocationType(couponQrData.location.type);

        // Basic info - Coupon - Location - Complete
        if (couponQrData.location.type === LOCATION_TYPE.FULL) {
          form.setValue('basicInfo.coupon.location.complete.searchAddress', getDefaultAddress(couponQrData.location));
          form.setValue('basicInfo.coupon.location.complete.state', couponQrData.location.state ?? '');
          form.setValue('basicInfo.coupon.location.complete.street', couponQrData.location.street ?? '');
          form.setValue('basicInfo.coupon.location.complete.city', couponQrData.location.city ?? '');
          form.setValue('basicInfo.coupon.location.complete.country', couponQrData.location.country ?? '');
          form.setValue(
            'basicInfo.coupon.location.complete.number',
            typeof couponQrData.location.number === 'number' ? String(couponQrData.location.number) : '',
          );
          form.setValue('basicInfo.coupon.location.complete.postalCode', couponQrData.location.postalCode ?? '');
          setIsManuallyLocationCompleteAddress(true);
        }
        // Basic info - Coupon - Location - URL
        if (couponQrData.location.type === LOCATION_TYPE.URL) {
          form.setValue('basicInfo.coupon.location.url', couponQrData.location.url ?? '');
        }
        // Basic info - Coupon - Location - Coordinates
        if (couponQrData.location.type === LOCATION_TYPE.COORDINATES) {
          form.setValue('basicInfo.coupon.location.coordinates.longitude', couponQrData.location.longitude);
          form.setValue('basicInfo.coupon.location.coordinates.latitude', couponQrData.location.latitude);
        }
      }
    }
  }, [isCouponData, isEditMode]);

  usePrefillQrCodeData({ qrCodeData, isQRCodeData, setValue: form.setValue });

  const onSubmitSuccess = async (values: CouponQRSchema) => {
    if (createOrUpdateQRError) {
      setCreateOrUpdateQrError(null);
    }

    const qrCodeDataPayload = getUpdateDynamicQrCodePayload({
      values: {
        settings: values.settings,
        qrCodeName: values.qrCodeName,
        qrCodeFolder: values.qrCodeFolder,
        passwordProtection: values.passwordProtection,
      },
      qrCodeData,
    });

    if (couponId) {
      const payload: UpdateCouponQrPayload = {
        title: values.basicInfo.offer.title,
        button_text: values.basicInfo.coupon.buttonText,
        button_url: values.basicInfo.coupon.url,
        coupon_code: values.basicInfo.coupon.code,
        terms_and_conditions: values.basicInfo.coupon.terms,
        valid_until: values.basicInfo.coupon.validUntil.toISOString(),
        use_barcode: values.basicInfo.coupon.useBarcode,
        show_code_button_text: values.basicInfo.offer.codeButton,
        badge_text: values.basicInfo.offer.salesBadge,
        description: values.basicInfo.offer.description,
        company_name: values.basicInfo.offer.company,
        // banner_background_color: '', TODO probably should be removed
        // ios_header_color: '', TODO probably should be removed
        fonts_colors: {
          title: values.appearance.fonts.titleColor,
          text: values.appearance.fonts.textsColor,
        },
        fonts: {
          title: values.appearance.fonts.title,
          text: values.appearance.fonts.text,
        },
        design: {
          primary: values.appearance.design.primary,
          secondary: values.appearance.design.secondary,
        },
      };

      if (locationType === LOCATION_TYPE.FULL) {
        if (showCompleteUrl && values.basicInfo.coupon.location.complete.searchAddress) {
          payload.location = {
            type: locationType,
            ...(values.basicInfo.coupon.location.complete.street
              ? { street: values.basicInfo.coupon.location.complete.street }
              : {}),
            ...(values.basicInfo.coupon.location.complete.number
              ? { number: Number(values.basicInfo.coupon.location.complete.number) }
              : {}),
            ...(values.basicInfo.coupon.location.complete.postalCode
              ? { postal_code: values.basicInfo.coupon.location.complete.postalCode }
              : {}),
            ...(values.basicInfo.coupon.location.complete.city
              ? { city: values.basicInfo.coupon.location.complete.city }
              : {}),
            ...(values.basicInfo.coupon.location.complete.state
              ? { state: values.basicInfo.coupon.location.complete.state }
              : {}),
            ...(values.basicInfo.coupon.location.complete.country
              ? { country: values.basicInfo.coupon.location.complete.country }
              : {}),
          };
        } else if (couponQrData?.location) {
          payload.location = {
            type: locationType,
            street: '',
            state: '',
            city: '',
            country: '',
            number: null,
            postal_code: '',
          };
        }
      } else if (locationType === LOCATION_TYPE.URL) {
        if (values.basicInfo.coupon.location.url) {
          payload.location = {
            type: locationType,
            url: values.basicInfo.coupon.location.url,
          };
        }
      } else if (locationType === LOCATION_TYPE.COORDINATES) {
        if (
          values.basicInfo.coupon.location.coordinates.latitude &&
          values.basicInfo.coupon.location.coordinates.longitude
        ) {
          payload.location = {
            type: locationType,
            longitude: values.basicInfo.coupon.location.coordinates.longitude,
            latitude: values.basicInfo.coupon.location.coordinates.latitude,
          };
        } else if (couponQrData?.location) {
          payload.location = {
            type: locationType,
            longitude: '',
            latitude: '',
          };
        }
      }

      if (banner.image) {
        if (banner.image !== couponQrData?.banner?.image && banner.imageId) {
          // Update only if banner image was changed and user uploaded new image
          payload.banner = {
            image_id: banner.imageId,
            scale: banner.scale,
            border_radius: banner.borderRadius,
            rotate: banner.rotate,
          };
        }
      } else {
        payload.banner = {};
      }

      if (barcode.image && values.basicInfo.coupon.useBarcode) {
        if (barcode.image !== couponQrData.barcode?.image && barcode.imageId) {
          // Update only if barcode image was changed and user uploaded new image
          payload.barcode = {
            image_id: barcode.imageId,
            scale: barcode.scale,
            border_radius: barcode.borderRadius,
            rotate: barcode.rotate,
          };
        }
      } else if (couponQrData?.barcode?.image) {
        payload.barcode = {}; // delete image in DB if barcode image was before and was deleted by user
      }

      await updateCouponQRMutate({
        payload,
        id: couponId,
      });

      if (Object.keys(qrCodeDataPayload).length) {
        //  must be called after update of coupon
        await updateQrCodeMutateAsync(qrCodeDataPayload);
      }

      setActiveStep(2);
      setPreviewViewType(PREVIEW_BAR_VIEW_TYPE.QRCODE);
    } else {
      const payload: CreateCouponQrPayload = {
        qr_code_id: Number(qrCodeId),
        title: values.basicInfo.offer.title,
        button_text: values.basicInfo.coupon.buttonText,
        button_url: values.basicInfo.coupon.url,
        terms_and_conditions: values.basicInfo.coupon.terms,
        valid_until: values.basicInfo.coupon.validUntil.toISOString(),
        use_barcode: values.basicInfo.coupon.useBarcode,
        show_code_button_text: values.basicInfo.offer.codeButton,
        badge_text: values.basicInfo.offer.salesBadge,
        description: values.basicInfo.offer.description,
        company_name: values.basicInfo.offer.company,
        // banner_background_color: '', TODO probably should be removed
        // ios_header_color: '', TODO probably should be removed
        fonts_colors: {
          title: values.appearance.fonts.titleColor,
          text: values.appearance.fonts.textsColor,
        },
        fonts: {
          title: values.appearance.fonts.title,
          text: values.appearance.fonts.text,
        },
        design: {
          primary: values.appearance.design.primary,
          secondary: values.appearance.design.secondary,
        },
      };

      if (locationType === LOCATION_TYPE.FULL) {
        if (showCompleteUrl && values.basicInfo.coupon.location.complete.searchAddress) {
          payload.location = {
            type: locationType,
            ...(values.basicInfo.coupon.location.complete.street
              ? { street: values.basicInfo.coupon.location.complete.street }
              : {}),
            ...(values.basicInfo.coupon.location.complete.number
              ? { number: Number(values.basicInfo.coupon.location.complete.number) }
              : {}),
            ...(values.basicInfo.coupon.location.complete.postalCode
              ? { postal_code: values.basicInfo.coupon.location.complete.postalCode }
              : {}),
            ...(values.basicInfo.coupon.location.complete.city
              ? { city: values.basicInfo.coupon.location.complete.city }
              : {}),
            ...(values.basicInfo.coupon.location.complete.state
              ? { state: values.basicInfo.coupon.location.complete.state }
              : {}),
            ...(values.basicInfo.coupon.location.complete.country
              ? { country: values.basicInfo.coupon.location.complete.country }
              : {}),
          };
        }
      } else if (locationType === LOCATION_TYPE.URL) {
        if (values.basicInfo.coupon.location.url) {
          payload.location = {
            type: locationType,
            url: values.basicInfo.coupon.location.url,
          };
        }
      } else if (locationType === LOCATION_TYPE.COORDINATES) {
        if (
          values.basicInfo.coupon.location.coordinates.latitude &&
          values.basicInfo.coupon.location.coordinates.longitude
        ) {
          payload.location = {
            type: locationType,
            longitude: values.basicInfo.coupon.location.coordinates.longitude,
            latitude: values.basicInfo.coupon.location.coordinates.latitude,
          };
        }
      }

      if (banner.imageId) {
        payload.banner = {
          image_id: banner.imageId,
          scale: banner.scale,
          border_radius: banner.borderRadius,
          rotate: banner.rotate,
        };
      }

      if (values.basicInfo.coupon.useBarcode) {
        if (barcode.imageId) {
          payload.barcode = {
            image_id: barcode.imageId,
            scale: barcode.scale,
            border_radius: barcode.borderRadius,
            rotate: barcode.rotate,
          };
        }
      } else {
        payload.coupon_code = values.basicInfo.coupon.code;
      }

      await createCouponQRMutate(payload);

      if (Object.keys(qrCodeDataPayload).length) {
        await updateQrCodeMutateAsync(qrCodeDataPayload);
      }

      setActiveStep(2);
      setPreviewViewType(PREVIEW_BAR_VIEW_TYPE.QRCODE);
    }
  };

  const handleSubmit = form.handleSubmit(onSubmitSuccess, (errors) => {
    const formRefs = [
      'qrCodeName',
      'appearance.design.primary',
      'appearance.design.secondary',
      'appearance.fonts.title',
      'appearance.fonts.text',
      'appearance.fonts.titleColor',
      'appearance.fonts.textsColor',
      'basicInfo.offer.company',
      'basicInfo.offer.title',
      'basicInfo.offer.description',
      'basicInfo.offer.salesBadge',
      'basicInfo.offer.codeButton',
      'basicInfo.coupon.useBarcode',
      'basicInfo.coupon.code',
      'basicInfo.coupon.validUntil',
      'basicInfo.coupon.buttonText',
      'basicInfo.coupon.url',
      'basicInfo.coupon.location.complete.searchAddress',
      'basicInfo.coupon.location.complete.street',
      'basicInfo.coupon.location.complete.number',
      'basicInfo.coupon.location.complete.postalCode',
      'basicInfo.coupon.location.complete.city',
      'basicInfo.coupon.location.complete.city',
      'basicInfo.coupon.location.complete.state',
      'basicInfo.coupon.location.complete.country',
      'basicInfo.coupon.location.url',
      'basicInfo.coupon.location.coordinates.longitude',
      'basicInfo.coupon.location.coordinates.latitude',
      'settings.isAutoGeneratedUrl',
      'settings.qrCodeUrl',
      'settings.isScanLimitsEnabled',
      'settings.scanLimit',
    ];
    const errorsRefs = getReactHookFormErrorsRefNames(errors);
    const firstError = formRefs.find((ref) => {
      return errorsRefs.some((errorRef) => errorRef === ref);
    });
    const elementWithFirstError = document.getElementById(firstError);
    if (elementWithFirstError) {
      elementWithFirstError.scrollIntoView();
    }
  });

  const showDesignStep = activeStep === 2 || activeStep === 3;

  return (
    <FormProvider {...form}>
      {activeStep === 1 && (
        <>
          {isEditMode && (isLoadingCouponQrData || isLoadingQrCodeData) ? (
            <Box sx={styles.loaderWrapper}>
              <Loader />
            </Box>
          ) : (
            <BuilderSecondStepLayout
              onNext={handleSubmit}
              qrType={QRCODE_DYNAMIC_MICROSITE_TYPE.COUPON}
              isNextProcessing={isPendingCreateCouponQR || isPendingUpdateCouponQR || isPendingUpdateQrCode}
            >
              <CouponTemplateAppearance />
              <CouponTemplateBasicInformation />
            </BuilderSecondStepLayout>
          )}
        </>
      )}
      {showDesignStep && <CreateQrDesignQRStepV2 />}
    </FormProvider>
  );
}

function useStyles() {
  return {
    loaderWrapper: css`
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
    `,
  };
}
